import { Box, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import PropTypes from "prop-types";
import type { ReactNode } from "react";

interface TwoLineListItem {
  subtitle: string;
  content: ReactNode;
}

interface TwoLineListSectionProps {
  title: string;
  items: TwoLineListItem[];
  height?: string | number;
  containerSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
}

const TwoLineListSection: React.FC<TwoLineListSectionProps> = ({
  title,
  items,
  height = "100%",
  containerSx,
  titleSx,
}) => {
  const baseContainerSx = {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "16px",
    gap: "10px",
    height: height,
    backgroundColor: "#FFFFFF",
    border: "1px solid #E8E8E9",
    borderRadius: "12px",
    flex: 1,
  } as const;

  const containerSxResolved: SxProps<Theme> = Array.isArray(containerSx)
    ? [baseContainerSx, ...containerSx]
    : containerSx
      ? [baseContainerSx, containerSx]
      : baseContainerSx;

  const baseTitleSx = {
    width: "100%",
    height: "48px",
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.15px",
    color: "#757575",
    flex: "none",
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
    display: "flex",
    alignItems: "flex-start",
    paddingTop: 0,
  } as const;

  const titleSxResolved: SxProps<Theme> = Array.isArray(titleSx)
    ? [baseTitleSx, ...titleSx]
    : titleSx
      ? [baseTitleSx, titleSx]
      : baseTitleSx;

  return (
    <Box sx={containerSxResolved}>
      <Typography variant="h6" sx={titleSxResolved}>
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "0px",
          width: "100%",
          flex: "1 1 auto",
          overflow: "visible",
        }}
      >
        {items.map((item, index) => (
          <Box
            key={`${item.subtitle}-${index}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0px",
              gap: "4px",
              height: "auto",
              marginBottom: "10px",
              "&:last-child": {
                marginBottom: 0,
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                width: "100%",
                height: "16px",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.5px",
                color: "#000000",
                flex: "none",
                order: 0,
                alignSelf: "stretch",
                flexGrow: 0,
              }}
            >
              {item.subtitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                width: "100%",
                height: "auto",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.4px",
                color: "#000000",
                flex: "none",
                order: 1,
                alignSelf: "stretch",
                flexGrow: 0,
              }}
            >
              {item.content}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

TwoLineListSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      subtitle: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    }).isRequired
  ).isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerSx: PropTypes.object,
  titleSx: PropTypes.object,
};

export default TwoLineListSection;
